import ConnectShopify, { CONNECT_SHOPIFY_STORAGE_KEY, ShopifyStore } from '@/components/ConnectShopify';
import {
    ForNextPulseModal, PaySuccessModal, PricingModal
} from '@/components/Pricing';
import { usePricingWarnDialog } from '@/components/Pricing/warnDialog';
import { useOnboarding } from '@/contexts/AuthContext/useOnboarding';
import { useDialog } from '@/hooks/useDialog';
import {
    AUTH_STORAGE_KEY,
    authService, supabase, VerifyAuthResponse
} from '@common/api/authService';
import JoinBetaModal from '@common/components/JoinBetaModal';
import { useBetaAccess } from '@common/hooks/useBetaAccess';
import { useUserStorage } from '@common/hooks/useUserStorage';
import { User } from '@supabase/supabase-js';
//@ts-ignore
import Cookies from 'js-cookie';
import React, {
    createContext, useContext, useEffect,
    useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { NangoIntegrations, useNango } from './useNango';
import { SyncStatusState, useSyncStatus } from './useSyncStatus';
import { UserBilling, useUserBilling } from './useUserBilling';



declare global {
    interface Window {
        isShopify: boolean;
    }
}

interface AuthContextType {
    openSignInModal: () => void;
    joinBetaModalOpen: boolean;
    openJoinBetaModal: () => void;
    closeJoinBetaModal: () => void;
    isConnectShopifyOpen: boolean;
    openConnectShopify: () => void;
    closeConnectShopify: () => void;
    isPricingModalOpen: boolean;
    openPricingModal: () => void;
    closePricingModal: () => void;
    isWelcomeModalOpen: boolean;
    openWelcomeModal: () => void;
    closeWelcomeModal: () => void;
    isOnboardingOpen: boolean;
    openOnboarding: () => void;
    closeOnboarding: () => void,
    openForNextPulseModal: () => void,
    logout: () => void,
    user: User | null;
    isBetaAccess: boolean;
    profile: VerifyAuthResponse | null;
    setProfile: (profile: VerifyAuthResponse | null) => void;
    userBilling: UserBilling;
    nangoIntegrations: NangoIntegrations;
    syncStatus: SyncStatusState;
    isLoadingProfile: boolean;
    // shopifyEmbed: ReturnType<typeof useShopifyEmbed>;
}

// 创建一个默认值
const defaultContextValue: AuthContextType = {
    openSignInModal: () => { },
    joinBetaModalOpen: false,
    openJoinBetaModal: () => { },
    closeJoinBetaModal: () => { },
    isConnectShopifyOpen: false,
    openConnectShopify: () => { },
    closeConnectShopify: () => { },
    isPricingModalOpen: false,
    openPricingModal: () => { },
    closePricingModal: () => { },
    isWelcomeModalOpen: false,
    openWelcomeModal: () => { },
    closeWelcomeModal: () => { },
    isOnboardingOpen: false,
    openOnboarding: () => { },
    closeOnboarding: () => { },
    openForNextPulseModal: () => { },
    logout: () => { },
    user: null,
    isBetaAccess: false,
    profile: null,
    setProfile: () => {},
    isLoadingProfile: false,
    userBilling: {
        userPlan: null,
        userPoints: null,
        setUserPlan: () => {},
        pointsHistory: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            data: []
        },
        invoices: {
            pagination: {
                total: 0,
                size: 0,
                totalPages: 0,
                hasPrevious: false,
                hasNext: false,
                page: 0
            },
            invoices: []
        },
        loading: {
            plan: false,
            points: false,
            history: false,
            invoices: false
        },
        error: {
            plan: null,
            points: null,
            history: null,
            invoices: null
        },
        fetchUserPlan: async () => Promise.resolve(),
        fetchUserPoints: async () => Promise.resolve(),
        fetchPointsHistory: async () => Promise.resolve(),
        fetchInvoices: async () => Promise.resolve(),
        refreshAll: async () => Promise.resolve(),
        planText: {
            planDisplayText: '',
            buttonText: '',
            expirationText: '',
            pricingButtongText: ''
        }
    },
    nangoIntegrations: {} as any,
    syncStatus: {
        syncStatuses: [],
        loading: false,
        syncCompleted: false
    },
    // shopifyEmbed: {
    //     isEmbedded: false,
    //     isAppBridgeReady: false,
    //     shopify: null,
    //     shopDomain: null,
    //     redirectToShopify: () => {}
    // }
};

// 使用默认值创建 Context
export const AuthContext = createContext<AuthContextType>(defaultContextValue);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [joinBetaModalOpen, setJoinBetaModalOpen] = useState(false);
    const [isConnectShopifyOpen, setIsConnectShopifyOpen] = useState(false);
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
    const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [isBetaAccess, setIsBetaAccess] = useBetaAccess();
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);
    const [profile, setProfile] = useState<VerifyAuthResponse | null>(null);
    const [stores, setStores] = useLocalStorage<ShopifyStore[]>(CONNECT_SHOPIFY_STORAGE_KEY, []);

    const userBilling = useUserBilling();
    // const shopifyEmbed = useShopifyEmbed();

    const {
        isOnboardingOpen, openOnboarding, closeOnboarding 
    } = useOnboarding(profile, isLoadingProfile);
    // const  hasShowFreeTrial = useRef(localStorage.getItem(HAS_SHOW_FREE_TRIAL) === 'true');
    
    const navigate = useNavigate();

    const openSignInModal = () => {
        navigate(`/auth?redirect=${window.location.href}`);
    };

    const { showDialog, DialogPortal } = useDialog();

    const openJoinBetaModal = () => {
        setJoinBetaModalOpen(true);
    };

    const closeJoinBetaModal = () => {
        setJoinBetaModalOpen(false);
    };

    const openConnectShopify = () => {
        setIsConnectShopifyOpen(true);
    };

    const closeConnectShopify = () => {
        setIsConnectShopifyOpen(false);
    };

    const openPricingModal = () => {
        setIsPricingModalOpen(true);
    };

    const closePricingModal = () => {
        setIsPricingModalOpen(false);
    };

    const openWelcomeModal = () => {
        setIsWelcomeModalOpen(true);
    };

    const closeWelcomeModal = () => {
        setIsWelcomeModalOpen(false);
    };

    const {
        dialog, openExpiredDialog, openCancelDialog 
    } = usePricingWarnDialog(openPricingModal);

    const _logout = async () => {
        Cookies.remove(AUTH_STORAGE_KEY);
        try {
            const result = await supabase.auth.signOut();
            if(result.error){
                throw result.error;
            }else{
                localStorage.removeItem(AUTH_STORAGE_KEY);
                window.location.href = '/'
            }
        } catch (error) {
            console.error('Logout failed:', error);
            localStorage.removeItem(AUTH_STORAGE_KEY);
            window.location.href = '/';
        }
    };

    const logout = async () => {
        // Show a confirmation dialog
        const confirmed = await showDialog({
            title: 'Are you sure to log out?',
            subtitle: `Log out of ${profile?.email || 'your account'}`,
            confirmText: 'Log out',
            cancelText: 'Stay logged in',
            onConfirm: () => _logout(),
        });
    }

    useEffect(() => {
        const init = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                try {
                    const verifyAuth = await authService.verifyAuth();
                    userBilling.refreshAll();
                    // setIsBetaAccess(!!verifyAuth.betaAccess);
                    setProfile(verifyAuth);
                    Cookies.set(AUTH_STORAGE_KEY, session.access_token, {
                        path: '/',        // 全站有效
                        sameSite: 'lax',  // 或 'none' + secure
                        secure: true      // HTTPS 必须
                    })
                    authService.getBetaStatus();
                } catch (error) {
                    if(error === '401'){
                        logout();
                        openSignInModal();
                    }
                    console.error('Verify auth failed:', error);
                }finally{
                    setIsLoadingProfile(false);
                }
            }else{
                setIsLoadingProfile(false);
            }
        }

        init();
    }, [])

    // 处理支付结果
    useEffect(() => {
        if(!profile){
            return;
        }
        const pay_result = new URLSearchParams(window.location.search).get('pay_result');
        if(pay_result === 'success'){
            openWelcomeModal();
            window.history.replaceState({}, '', window.location.pathname);
            // 防止支付更新延迟
            setTimeout(() => {
                userBilling.fetchUserPlan();
            }, 1500);
        }
        if(pay_result === 'cancel'){
            // openCancelDialog();
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [profile])


    useEffect(() => {
        const url = new URL(location.href);
        const storeUrl = url.searchParams.get('shop') || '';
        const storeToken = url.searchParams.get('shopify_token') || '';
        if(storeUrl && storeToken) {
            setStores(prev => [...prev.filter(v => v.storeUrl !== storeUrl), { storeUrl, storeToken }]);
        }
    }, [])

    const [isOpenForNextPulse, setIsOpenForNextPulse] = useState(false);
    const { get, set } = useUserStorage();
    
    const openForNextPulseModal = () => {
        if(userBilling?.userPlan?.planCode !== 'FREE'){
            return;
        }
        const FOR_NEXT_PULSE_SHOWN_KEY = 'for_next_pulse_shown';
        // Check if welcome modal has been shown before
        const hasShownWelcome = get(FOR_NEXT_PULSE_SHOWN_KEY, false);
        if(!hasShownWelcome){
            setIsOpenForNextPulse(true);
            // Mark welcome as shown when modal is opened
            set(FOR_NEXT_PULSE_SHOWN_KEY, true);
        }
    }

    // // 处理订阅过期
    // useEffect(() => {
    //     if(userBilling.userPlan?.endAt && new Date(userBilling.userPlan.endAt) < new Date()){
    //         openExpiredDialog();
    //         localStorage.setItem(HAS_SHOW_FREE_TRIAL_EXPIRED_SOON, 'true');
    //     }
    // }, [userBilling.userPlan])

    // onboarding logic is now handled by useOnboarding hook

    // 检查用户是否在7天内过期
    // useEffect(() => {
    //     if(!userBilling?.userPlan?.endAt){
    //         return;
    //     }
    //     const endDate = new Date(userBilling?.userPlan?.endAt);
    //     const now = new Date();
    //     const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    //     if(daysUntilExpiry > 7){
    //         return;
    //     }
    //     const hasShowFreeTrialExpiredSoon = localStorage.getItem(HAS_SHOW_FREE_TRIAL_EXPIRED_SOON) === 'true';

    //     if(!hasShowFreeTrialExpiredSoon ){
    //         openPricingModal();
    //         localStorage.setItem(HAS_SHOW_FREE_TRIAL_EXPIRED_SOON, 'true');
    //     }

    // }, [userBilling.userPlan])


    // 监听 Supabase 的 auth 状态变化
    useEffect(() => {
        // 监听 auth 状态变化
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_IN' && session?.user) {
                setUser(session.user);
            }else if (event === 'SIGNED_OUT') {
                setUser(null);
                // setIsBetaAccess(false);
            }else {
                console.log("supabse event", event)
            }
        });

        // 清理订阅
        return () => subscription.unsubscribe();
    }, []);

    const nangoIntegrations = useNango(user);
    const syncStatus = useSyncStatus(user?.id);

    const value = {
        openSignInModal,
        joinBetaModalOpen,
        openJoinBetaModal,
        closeJoinBetaModal,
        isConnectShopifyOpen,
        openConnectShopify,
        closeConnectShopify,
        isPricingModalOpen,
        openPricingModal,
        closePricingModal,
        isWelcomeModalOpen,
        openWelcomeModal,
        closeWelcomeModal,
        isOnboardingOpen,
        openOnboarding,
        closeOnboarding,
        openForNextPulseModal,
        logout,
        user,
        isBetaAccess,
        profile,
        setProfile,
        userBilling,
        nangoIntegrations,
        syncStatus,
        isLoadingProfile,
        // shopifyEmbed
    };
    return (
        <AuthContext.Provider value={value}>
            {children}
            <JoinBetaModal
                showModal={joinBetaModalOpen}
                onClose={closeJoinBetaModal}
            />
            <ConnectShopify
                isOpen={isConnectShopifyOpen}
                onClose={closeConnectShopify}
            />
            <PricingModal
                isOpen={isPricingModalOpen}
                onClose={closePricingModal}
                fisrt={false}
            />
            <PaySuccessModal
                isOpen={isWelcomeModalOpen}
                onClose={closeWelcomeModal}
            />
            <ForNextPulseModal 
                isOpen={isOpenForNextPulse}
                onClose={() => setIsOpenForNextPulse(false)}
            />
            {dialog}
            <DialogPortal />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};